import { type ReactNode, forwardRef } from 'react';
import { clsx } from 'clsx';

import styles from './Button.module.scss';

import Loader from '@/components/common/Loader';

const BUTTON_STYLES = {
  primary: 'primary',
  secondary: 'secondary',
  tertiary: 'tertiary',
  white: 'white',
} as const;

type OneOfButtonStyles = (typeof BUTTON_STYLES)[keyof typeof BUTTON_STYLES];

interface ButtonProps extends React.HTMLProps<HTMLButtonElement> {
  children?: ReactNode;
  onClick?: () => void;
  className?: string;
  kind?: OneOfButtonStyles;
  type?: 'button' | 'submit';
  loading?: boolean;
}

const Button = forwardRef<HTMLButtonElement, ButtonProps>(
  ({ children, onClick, kind = BUTTON_STYLES.secondary, className = '', type = 'button', loading, ...props }, ref) => (
    // eslint-disable-next-line react/button-has-type -- warning message is too pretentious
    <button className={clsx(styles.button, styles[kind], className)} type={type} onClick={onClick} {...props} ref={ref}>
      {loading ? (
        <Loader size={24} white={kind === BUTTON_STYLES.primary} />
      ) : (
        // wrapper protects React from crashing on text nodes replaced by page translators (e.g. Google Translate)
        <span className={styles.content}>{children}</span>
      )}
    </button>
  ),
);
Button.displayName = 'Button';

interface LinkButtonProps extends React.HTMLProps<HTMLAnchorElement> {
  children: ReactNode;
  className?: string;
  kind?: OneOfButtonStyles;
  type?: 'button' | 'submit';
  loading?: boolean;
}

const LinkButton = ({
  children,
  kind = BUTTON_STYLES.secondary,
  className = '',
  loading,
  ...props
}: LinkButtonProps) => (
  <a className={clsx(styles.button, styles[kind], className)} {...props}>
    {loading ? (
      <Loader size={24} white={kind === BUTTON_STYLES.primary} />
    ) : (
      <span className={styles.content}>{children}</span>
    )}
  </a>
);

export { Button, LinkButton };
